import type { CSSObject } from '@emotion/react'

import type { ResolvedTheme } from './themes'
import { textStyle } from './typography.helpers'

export type ResponsiveFieldStyle = {
  base: CSSObject
  desktop?: CSSObject
}

export type FieldPresentation = {
  frame: ResponsiveFieldStyle
  focused: CSSObject
  error: CSSObject
  value: ResponsiveFieldStyle
  caretColor: string
  placeholderWeight: number
  disabledColor: string
  adornmentColor: string
  disabledAdornmentColor: string
  label: ResponsiveFieldStyle
  floatingLabel: ResponsiveFieldStyle
  labelText: ResponsiveFieldStyle
  placeholder: ResponsiveFieldStyle
  helper: CSSObject
  helperText: ResponsiveFieldStyle
  disabledCaptionColor: string
}

type Foundations = Pick<ResolvedTheme, 'palette' | 'spacing' | 'typography'>

export const modernFieldPresentation = ({
  palette,
  spacing,
  typography,
}: Foundations): FieldPresentation => {
  const value = {
    base: {
      ...textStyle(typography.app.bodyMobile),
      fontWeight: typography.fontWeight.medium,
    },
    desktop: textStyle(typography.app.body),
  }
  const caption = {
    base: textStyle(typography.app.bodySmMobile),
    desktop: textStyle(typography.app.bodySm),
  }

  return {
    frame: {
      base: {
        borderBottom: '2px solid transparent',
        boxShadow: `inset 0 -1px 0 ${palette.ghost.mediumContrast}`,
        boxSizing: 'border-box',
        padding: `${spacing[4]} 0`,
      },
    },
    focused: {
      borderBottomColor: palette.accent.highContrast,
      boxShadow: 'none',
    },
    error: { borderBottomColor: palette.error.main, boxShadow: 'none' },
    value,
    caretColor: palette.secondary.maxContrast,
    placeholderWeight: typography.fontWeight.medium,
    disabledColor: palette.text.disabled,
    adornmentColor: palette.text.body,
    disabledAdornmentColor: palette.text.disabled,
    label: {
      base: {
        color: palette.text.body,
        height: `${typography.app.bodySmMobile.lineHeight}px`,
      },
      desktop: { height: `${typography.app.bodySm.lineHeight}px` },
    },
    floatingLabel: {
      base: {
        top: `calc(${typography.app.bodySmMobile.lineHeight}px + ${spacing[4]})`,
      },
      desktop: {
        top: `calc(${typography.app.bodySm.lineHeight}px + ${spacing[4]})`,
      },
    },
    labelText: caption,
    placeholder: {
      ...value,
      base: { ...value.base, color: palette.text.emptyState },
    },
    helper: { padding: `${spacing[4]} 0`, color: palette.text.description },
    helperText: caption,
    disabledCaptionColor: palette.text.disabled,
  }
}

export const legacyFieldPresentation = ({
  palette,
  spacing,
  typography,
}: Foundations): FieldPresentation => {
  // Fixed input pixels preserve the original iOS zoom protection; captions use rem.
  const value = {
    base: {
      fontSize: '16px',
      lineHeight: '24px',
      fontWeight: typography.fontWeight.medium,
    },
  }
  const caption = {
    base: typography.variants?.b3 ?? {
      fontSize: '0.875rem',
      lineHeight: '1.375rem',
      letterSpacing: '-0.3px',
    },
  }

  return {
    frame: {
      base: {
        borderBottom: `1px solid ${palette.ghost.mediumContrast}`,
        boxSizing: 'content-box',
        minHeight: '22px',
        padding: `0 0 ${spacing[4]}`,
      },
      desktop: { minHeight: '24px', padding: `${spacing[4]} 0 ${spacing[8]}` },
    },
    focused: {
      borderBottomColor: palette.secondary.mainContrast,
      boxShadow: `0 1px 0 ${palette.secondary.mainContrast}`,
    },
    error: {
      borderBottomColor: palette.error.main,
      boxShadow: `0 1px 0 ${palette.error.main}`,
    },
    value,
    caretColor: palette.secondary.mainContrast,
    placeholderWeight: typography.fontWeight.regular,
    disabledColor: palette.text.emptyState,
    adornmentColor: palette.deepPurple[700],
    disabledAdornmentColor: palette.deepPurple[600],
    label: {
      base: {
        color: palette.tertiary.mediumContrastAlternative,
        height: '16px',
      },
      desktop: { height: '18px' },
    },
    floatingLabel: { base: { top: '16px' }, desktop: { top: '22px' } },
    labelText: caption,
    placeholder: {
      base: {
        ...value.base,
        fontWeight: typography.fontWeight.regular,
        color: palette.tertiary.mediumContrastAlternative,
      },
    },
    helper: {
      marginTop: spacing[4],
      color: palette.tertiary.mediumContrastAlternative,
    },
    helperText: caption,
    disabledCaptionColor: palette.ghost.mediumContrast,
  }
}
